// Taxes route - 2024
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import Login from './components/Login';
import QuickOrder from './components/QuickOrder';
import QuickOrderDeneme from './components/QuickOrderDeneme';
import TableGrid from './components/TableGrid';
import TableOrder from './components/TableOrder';
import OrderManagement from './components/OrderManagement';
import Payment from './components/Payment';
import Receipt from './components/Receipt';
import KitchenOrders from './components/KitchenOrders';
import CustomerScreen from './components/CustomerScreen';
import Taxes from './components/Taxes';
import './App.css';

// Lazy load heavy components
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));
const OriginalReports = React.lazy(() => import('./components/OriginalReports'));

// Protected Route bileşeni
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Role-based yönlendirme bileşeni
const RoleBasedRoute: React.FC = () => {
  const { user } = useAuth();
  
  if (user?.role === 'mutfak') {
    return <KitchenOrders />;
  }
  
  if (user?.role === 'customer') {
    return <CustomerScreen />;
  }
  
  return <QuickOrder />;
};

// Mutfak için özel korumalı route
const KitchenProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (user?.role !== 'mutfak') {
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
};

// Admin için özel korumalı route
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
};

// Ana uygulama içeriği
const AppContent: React.FC = () => {
  return (
    <Router>
      <div className="min-h-screen">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <RoleBasedRoute />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/deneme" 
            element={
              <ProtectedRoute>
                <QuickOrderDeneme />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kitchen" 
            element={
              <KitchenProtectedRoute>
                <KitchenOrders />
              </KitchenProtectedRoute>
            } 
          />
          <Route 
            path="/tables" 
            element={
              <ProtectedRoute>
                <TableGrid />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/order/:tableId" 
            element={
              <ProtectedRoute>
                <TableOrder />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/order/:tableId/:orderId" 
            element={
              <ProtectedRoute>
                <TableOrder />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/orders" 
            element={
              <ProtectedRoute>
                <OrderManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/payment/:orderId" 
            element={
              <ProtectedRoute>
                <Payment />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/receipt/:orderId" 
            element={
              <ProtectedRoute>
                <Receipt />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <AdminProtectedRoute>
                <Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
                  </div>
                }>
                  <AdminPanel />
                </Suspense>
              </AdminProtectedRoute>
            } 
          />
          <Route 
            path="/orj" 
            element={
              <AdminProtectedRoute>
                <Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown"></div>
                  </div>
                }>
                  <OriginalReports />
                </Suspense>
              </AdminProtectedRoute>
            } 
          />
          <Route 
            path="/taxes" 
            element={
              <AdminProtectedRoute>
                <Taxes />
              </AdminProtectedRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 1500,
          style: {
            background: '#8B4513',
            color: '#fff',
            fontSize: '14px',
            padding: '8px 12px',
            borderRadius: '6px',
            maxWidth: '300px',
          },
          success: {
            style: {
              background: '#22c55e',
              fontSize: '14px',
              padding: '8px 12px',
            },
          },
          error: {
            style: {
              background: '#ef4444',
              fontSize: '14px',
              padding: '8px 12px',
            },
          },
        }}
      />
    </Router>
  );
};

// CSRF token'ı al
export const initializeCSRF = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || window.location.origin}/api/csrf-token`, {
      credentials: 'include'
    });
    if (response.ok) {
      // CSRF token başarıyla alındı
      return true;
    } else {
      // CSRF token alınamadı
      return false;
    }
  } catch (error) {
    // CSRF token alınamadı
    return false;
  }
};

function App() {
  useEffect(() => {
    // Uygulama başlarken CSRF token'ı al
    initializeCSRF();
    
    // Her 5 dakikada bir CSRF token'ı yenile
    const csrfInterval = setInterval(async () => {
      const success = await initializeCSRF();
      if (!success) {
        // CSRF token yenilenemedi, tekrar deneniyor
        setTimeout(initializeCSRF, 30000);
      }
    }, 5 * 60 * 1000); // 5 dakika
    
    // Cleanup
    return () => {
      clearInterval(csrfInterval);
    };
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <CurrencyProvider>
        <AppContent />
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
